import React, { useEffect, useState } from "react";
import { useLang } from "@/context/LangContext";
import api, { getErrorDetail } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { format, parseISO } from "date-fns";
import { Trash2 } from "lucide-react";

export default function AnnouncementsPanel() {
  const { t } = useLang();
  const [form, setForm] = useState({ title:"", title_zh:"", content:"", content_zh:"", audience:"public" });
  const [items, setItems] = useState([]);
  const [posting, setPosting] = useState(false);

  async function load() {
    try { const { data } = await api.get("/announcements"); setItems(data); } catch { /* ignore */ }
  }
  useEffect(() => { (async () => { await load(); })(); }, []);

  async function submit(e) {
    e.preventDefault(); setPosting(true);
    try {
      await api.post("/announcements", form);
      toast.success("Posted");
      setForm({ title:"", title_zh:"", content:"", content_zh:"", audience: form.audience });
      load();
    } catch (e) { toast.error(getErrorDetail(e)); }
    finally { setPosting(false); }
  }

  async function del(id) {
    try { await api.delete(`/announcements/${id}`); toast.success("Deleted"); load(); }
    catch(e){ toast.error(getErrorDetail(e)); }
  }

  return (
    <div className="grid grid-cols-1 lg:grid-cols-12 gap-8">
      <form onSubmit={submit} className="lg:col-span-5 bg-white border border-[hsl(var(--border))] rounded-2xl p-6 space-y-4 h-fit" data-testid="admin-ann-form">
        <div>
          <Label className="text-xs uppercase tracking-[0.2em] text-[hsl(var(--muted-foreground))]">{t.admin.audience}</Label>
          <Select value={form.audience} onValueChange={(v)=>setForm({...form, audience:v})}>
            <SelectTrigger data-testid="admin-ann-audience" className="mt-2"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="public" data-testid="admin-ann-audience-public">{t.admin.audience_public}</SelectItem>
              <SelectItem value="staff" data-testid="admin-ann-audience-staff">{t.admin.audience_staff}</SelectItem>
              <SelectItem value="students_parents" data-testid="admin-ann-audience-students">{t.admin.audience_students}</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div>
          <Label className="text-xs uppercase tracking-[0.2em] text-[hsl(var(--muted-foreground))]">{t.admin.title_field}</Label>
          <Input required value={form.title} onChange={(e)=>setForm({...form, title:e.target.value})} data-testid="admin-ann-title" className="mt-2" />
        </div>
        <div>
          <Label className="text-xs uppercase tracking-[0.2em] text-[hsl(var(--muted-foreground))]">{t.admin.title_zh_field}</Label>
          <Input value={form.title_zh} onChange={(e)=>setForm({...form, title_zh:e.target.value})} data-testid="admin-ann-title-zh" className="mt-2" />
        </div>
        <div>
          <Label className="text-xs uppercase tracking-[0.2em] text-[hsl(var(--muted-foreground))]">{t.admin.content_field}</Label>
          <Textarea required rows={4} value={form.content} onChange={(e)=>setForm({...form, content:e.target.value})} data-testid="admin-ann-content" className="mt-2" />
        </div>
        <div>
          <Label className="text-xs uppercase tracking-[0.2em] text-[hsl(var(--muted-foreground))]">{t.admin.content_zh_field}</Label>
          <Textarea rows={3} value={form.content_zh} onChange={(e)=>setForm({...form, content_zh:e.target.value})} data-testid="admin-ann-content-zh" className="mt-2" />
        </div>
        <Button type="submit" disabled={posting} data-testid="admin-ann-submit" className="bg-[hsl(var(--primary))] hover:bg-[hsl(var(--primary))]/90 text-white rounded-full px-6">{posting ? "…" : t.admin.post}</Button>
      </form>

      <div className="lg:col-span-7">
        <h3 className="text-xs uppercase tracking-[0.25em] text-[hsl(var(--muted-foreground))] mb-4">{t.admin.recent}</h3>
        <ul className="divide-y divide-[hsl(var(--border))] bg-white border border-[hsl(var(--border))] rounded-2xl">
          {items.map((a) => (
            <li key={a.id} data-testid={`admin-ann-row-${a.id}`} className="p-5 flex items-start gap-4 justify-between">
              <div className="min-w-0">
                <div className="flex items-center gap-2 text-xs uppercase tracking-[0.2em] text-[hsl(var(--muted-foreground))]">
                  <span>{format(parseISO(a.created_at), "MMM dd")}</span>
                  <span>·</span>
                  <span className="text-[hsl(var(--primary))]">{a.audience}</span>
                </div>
                <h4 className="font-display text-lg tracking-tight mt-1">{a.title}</h4>
                <p className="text-sm text-[hsl(var(--muted-foreground))] mt-1 line-clamp-2">{a.content}</p>
              </div>
              <Button variant="ghost" size="sm" onClick={()=>del(a.id)} data-testid={`admin-ann-delete-${a.id}`} className="text-[hsl(var(--destructive))]"><Trash2 className="w-4 h-4" /></Button>
            </li>
          ))}
          {items.length === 0 && <li className="p-8 text-center text-[hsl(var(--muted-foreground))]">—</li>}
        </ul>
      </div>
    </div>
  );
}
